/**
 * 図で共通に使う部品と、全国の工程の帯グラフ（サンプルの monthTicks S940-947・portfolioGantt S1042-1049）。
 * 色・文字の大きさはサンプルと同じ値を属性で持つ（スタイルシートに頼らない）。
 */
import { clamp } from '../core/math';
import { escapeHtml, safeColor } from '../core/text';
import { DAY_MS } from '../core/time';
import type { DayNumber } from '../core/time';
import { B, T_MAX, inWinter } from '../model';
import type { Region } from '../model';
import type { SiteView } from '../app/store';

const utcDay = (y: number, m: number): DayNumber => Math.round(Date.UTC(y, m, 1) / DAY_MS);
const n1 = (v: number) => v.toFixed(1);

/** 通日を「YY/M」にする（サンプルの ym）。 */
export const ym = (n: DayNumber): string => {
  const d = new Date(Math.floor(n) * DAY_MS);
  return `${String(d.getUTCFullYear()).slice(2)}/${d.getUTCMonth() + 1}`;
};

/**
 * 月の目盛り線と「YY/M」の文字（サンプルの monthTicks）。`every` か月ごとに線と文字を出す。
 * winter を渡すと、冬期の月に薄い青の帯を敷く。
 */
export function monthTicks(
  t0: DayNumber,
  t1: DayNumber,
  X: (d: number) => number,
  y1: number,
  y2: number,
  every: number,
  winter: Region | null = null,
): string {
  const d0 = new Date(t0 * DAY_MS);
  let y = d0.getUTCFullYear();
  let m = d0.getUTCMonth() + 1;
  let k = 0;
  let h = '';
  for (;;) {
    const d = utcDay(y, m);
    if (d > t1) break;
    if (winter && inWinter(winter, d)) {
      const d2 = utcDay(m === 11 ? y + 1 : y, (m + 1) % 12);
      h += `<rect x="${n1(X(d))}" y="${y1}" width="${n1(Math.max(0, X(Math.min(d2, t1)) - X(d)))}" height="${y2 - y1}" fill="#60a5fa" opacity=".08" class="winter"/>`;
    }
    if (k++ % every === 0) {
      const x = n1(X(d));
      h += `<line x1="${x}" x2="${x}" y1="${y1}" y2="${y2}" stroke="#22304d" class="tick"/><text x="${n1(X(d) + 2)}" y="${y1 - 4}" font-size="9.5" fill="#93a1bd" class="ticklabel">${ym(d)}</text>`;
    }
    m++;
    if (m > 11) {
      m = 0;
      y++;
    }
  }
  return h;
}

/** 全現場の工程の帯グラフ（灰＝計画、色＝予測、濃い色＝表示日までの進み具合）。 */
export function portfolioGantt(views: readonly SiteView[], T: DayNumber): string {
  if (!views.length) return '<div class="empty">現場がありません</div>';
  const t0 = Math.min(...views.map((v) => v.site.planStart)) - 15;
  // 予測が表示期間の先へ出る極端な計画でも、横軸は表示期間の終わりで打ち切る（帯は右端に張りつく）
  const t1 =
    Math.min(Math.max(...views.map((v) => Math.max(v.site.planEnd, v.cur.end))), T_MAX) + 20;
  const W = 620;
  const LW = 96;
  const RH = 22;
  const top = 24;
  const H = top + views.length * RH + 6;
  const X = (d: number) => LW + ((clamp(d, t0, t1) - t0) / (t1 - t0)) * (W - LW - 10);
  let h = `<svg class="chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="全現場の工程（灰＝計画、色＝予測）">${monthTicks(t0, t1, X, top - 6, H - 4, 3)}`;
  views.forEach((v, i) => {
    const s = v.site;
    const y = top + i * RH;
    const x0 = X(s.planStart);
    const color = safeColor(s.color);
    h += `<text x="6" y="${y + 14}" font-size="10.5" fill="#e2e8f0" class="rowlabel">${escapeHtml(s.id.slice(2))} ${escapeHtml(s.pref.replace(/(都|府|県)$/, ''))}</text>
    <rect x="${n1(x0)}" y="${y + 2}" width="${n1(X(s.planEnd) - x0)}" height="4" rx="2" fill="#475569" class="planbar"/>
    <rect x="${n1(x0)}" y="${y + 8}" width="${n1(X(v.cur.end) - x0)}" height="10" rx="3" fill="${color}" fill-opacity=".3" stroke="${color}"/>
    <rect x="${n1(x0)}" y="${y + 8}" width="${n1((X(v.cur.end) - x0) * v.state.overall)}" height="10" rx="3" fill="${color}"/>`;
  });
  const xb = n1(X(B));
  const xt = n1(X(T));
  return `${h}<line x1="${xb}" x2="${xb}" y1="${top - 4}" y2="${H - 4}" stroke="#fff" stroke-dasharray="4 3" class="baseline"/><line x1="${xt}" x2="${xt}" y1="${top - 4}" y2="${H - 4}" stroke="#f59e0b" stroke-width="2" class="nowline"/></svg>`;
}
